import { FirebaseActions, ProgressSelectors, StatsSelectors, useTranslation } from '@web_opxp/features';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { SKELETON_CLASSNAME } from '../base/constants';
import { IState } from '../store/state';
import './TrainingCalendar.scss';
import TrainingCalendarDay from './TrainingCalendarDay';
import TrainingGoalDialog from './TrainingGoalDialog';
import { WEEK_GOAL_STAT_ID } from './constants';
import { IWeeklyTrainingData } from './types';
import { getMonday, parseDateString } from './utils';

interface ITrainingCalendarProps {
  loading: boolean;
  weekData: IWeeklyTrainingData[];
  streak?: number;
  onRender: () => void;
}

export function TrainingCalendarFC({ loading, streak, weekData, onRender }: Readonly<ITrainingCalendarProps>) {
  const { language, t } = useTranslation();
  const [weekDay, setWeekDay] = useState(-1);
  const loadingClassname = !language || !weekData || loading ? SKELETON_CLASSNAME : '';
  let weeklyExercises = 0;
  let daysPassed = 0;
  const days = weekData.map((data, index) => {
    if (data.exercisesDone) {
      weeklyExercises += data.exercisesDone;
      daysPassed += 1;
    }
    // week days in locas and in the Date object are starting from sunday
    const weekIndex = (index + 1) % 7;
    const clickHandler = () => {
      if (!data.isToday && typeof data.exercisesDone !== 'undefined') {
        // selecting days where exercises were done (or = 0)
        // is not allowed excepting for today
        return;
      }
      setWeekDay(weekIndex);
    };
    return (
      <TrainingCalendarDay
        weekDay={weekIndex}
        exercisesDone={data.exercisesDone}
        goal={data.goal}
        isToday={data.isToday}
        onClick={clickHandler}
        key={index}
      />
    );
  });
  useEffect(() => {
    if (loading || !onRender) {
      return;
    }
    onRender();
  }, [loading, onRender]);
  return (
    <div className="training-calendar">
      <div className="panel">
        <h2 className={loadingClassname}>{t('TrainingCalendar.Title')}</h2>
        <p className={loadingClassname}>{t('TrainingCalendar.TopDescription')}</p>
        <div className="training-calendar-week">{days}</div>
        <p className={loadingClassname}>
          {t('TrainingCalendar.BottomDescription', { weeklyExercises, daysPassed })}&nbsp;
          {streak && streak > 1 ? t('TrainingCalendar.StreakLabel', { streak }) : ''}
        </p>
      </div>
      <TrainingGoalDialog weekDay={weekDay} onClose={() => setWeekDay(-1)} />
    </div>
  );
}

const mapStateToProps = (state: IState) => {
  const loading = ProgressSelectors.isProgressLoading(state);
  let weekData: IWeeklyTrainingData[] = [...Array<IWeeklyTrainingData>(7).fill({})];
  let streak = 0;
  if (!loading) {
    const today = new Date();
    streak = ProgressSelectors.getStreakForDate(state, today);
    today.setHours(0, 0, 0, 0);
    const monday = getMonday(today);
    const todayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
    weekData = weekData.map((_, index) => {
      const date = new Date(monday);
      date.setDate(date.getDate() + index);
      const exercisesDone = index > todayIndex ? undefined : ProgressSelectors.getCompletionCountForDate(state, date);
      const weekDay = date.getDay();
      let goal: number | undefined;
      const goalSetDateString = StatsSelectors.getStatStringValueById(
        state,
        WEEK_GOAL_STAT_ID.replace('x', weekDay.toString())
      );
      if (goalSetDateString) {
        const goalSetDate = parseDateString(goalSetDateString);
        if (goalSetDate && goalSetDate.getTime() >= monday.getTime()) {
          goal = StatsSelectors.getStatIntValueById(state, WEEK_GOAL_STAT_ID.replace('x', weekDay.toString()));
        }
      }
      return { exercisesDone, goal, isToday: index === todayIndex };
    });
  }
  return {
    loading,
    weekData,
    streak,
  };
};

const mapDispatchToProps = {
  onRender: () => FirebaseActions.analyticsEventAction({ eventName: 'v_postworkout_view' }),
};

const TrainingCalendar = connect(mapStateToProps, mapDispatchToProps)(TrainingCalendarFC);
export default TrainingCalendar;
